import { useEffect, useState } from 'react';

import { type AnnouncePoliteness, subscribeToAnnouncements } from '@/lib/announcer';

/**
 * The single `aria-live` pair for the whole app, rendered once by `CashierLayout`.
 *
 * Both a polite and an assertive node are rendered, because `aria-live` cannot be changed on a live
 * node and have the change respected — screen readers latch the politeness when the region is first
 * seen. Two nodes with fixed politeness is the only reliable way to have both.
 *
 * Messages are cleared shortly after being set so that announcing the same string twice in a row
 * still produces two announcements. Without the clear, an identical text node is not a mutation and
 * the second copy confirmation would be silent.
 */
const CLEAR_DELAY_MS = 1_000;

export function LiveRegion() {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');

  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const setFor = (message: string, politeness: AnnouncePoliteness) => {
      const apply = politeness === 'assertive' ? setAssertive : setPolite;
      apply(message);
      const timer = setTimeout(() => {
        apply('');
        timers.delete(timer);
      }, CLEAR_DELAY_MS);
      timers.add(timer);
    };

    const unsubscribe = subscribeToAnnouncements(setFor);

    return () => {
      unsubscribe();
      for (const timer of timers) clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {polite}
      </div>
      <div role="alert" aria-live="assertive" aria-atomic="true" className="sr-only">
        {assertive}
      </div>
    </>
  );
}
